import Glide from "@glidejs/glide";
import useNcId from "hooks/useNcId";
import { FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { getCategories } from "data/api";
import SafeImage from "components/SafeImage/SafeImage";

interface ProductItem {
  id: number;
  name: string;
  image: string;
  href: string;
  desc: string;
}

const productMeta: Record<number, { desc: string; label?: string }> = {
  3: {
    label: "Shadow Gap Profiles",
    desc: "Recessed trim details for clean linear gaps in modern architecture.",
  },
  37: {
    label: "Expansion Joint Covers",
    desc: "Aluminium & stainless steel systems for structural movement.",
  },
  28: {
    label: "Aluminium Tile Trims",
    desc: "Luxury metal trim series for floors, walls and corners.",
  },
  11: {
    desc: "Aluminium and SS skirting profiles for seamless floor finishes.",
  },
  29: {
    desc: "Corrosion-resistant stainless trims for wet and heavy-duty areas.",
  },
  14: {
    desc: "Integrated LED skirting for ambient architectural lighting.",
  },
};

const fallbackProducts: ProductItem[] = [
  {
    id: 3,
    name: "Shadow Gap Profiles",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/shadow+gap+ceiling+profile.png",
    href: "/categories/3",
    desc: productMeta[3].desc,
  },
  {
    id: 37,
    name: "Expansion Joint Covers",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/Expansion+Joints/Expansion+Joint+Cat.jpg",
    href: "/categories/37",
    desc: productMeta[37].desc,
  },
  {
    id: 28,
    name: "Aluminium Tile Trims",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/ALUMINIUM+TT+CATEGORY.png",
    href: "/categories/28",
    desc: productMeta[28].desc,
  },
  {
    id: 11,
    name: "Skirtings",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/Skirting/BCR+SKIRTING+CATEGORY.png",
    href: "/categories/11",
    desc: productMeta[11].desc,
  },
  {
    id: 29,
    name: "SS Tile Trims",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/SS+TILE+TRIMS+CAT.png",
    href: "/categories/29",
    desc: productMeta[29].desc,
  },
  {
    id: 14,
    name: "LED Skirting Profiles",
    image:
      "https://bcrprofiles.s3.me-central-1.amazonaws.com/LED+SKIRTING+-+FLOATING+WALL/BCR+LED+Profiles.png",
    href: "/categories/14",
    desc: productMeta[14].desc,
  },
];

const featuredCategoryIds = [3, 37, 28, 11, 29, 14];

const SectionOurProducts: FC = () => {
  const UNIQUE_CLASS = "OurProducts" + useNcId();
  const [products, setProducts] = useState<ProductItem[]>(fallbackProducts);
  const [activeIndex, setActiveIndex] = useState(0);
  const glideRef = useRef<Glide | null>(null);

  useEffect(() => {
    getCategories().then((categories) => {
      if (!Array.isArray(categories) || categories.length === 0) return;

      const mapped = featuredCategoryIds
        .map((id) => categories.find((c: any) => c.id === id))
        .filter(Boolean)
        .map((c: any) => ({
          id: c.id,
          name: productMeta[c.id]?.label || c.title,
          image: c.image,
          href: `/categories/${c.id}`,
          desc: productMeta[c.id]?.desc || "Explore our full range of precision profiles.",
        }))
        .filter((p) => p.image);

      if (mapped.length > 0) setProducts(mapped);
    });
  }, []);

  useEffect(() => {
    const container = document.querySelector(`.${UNIQUE_CLASS}`);
    const slides = container?.querySelectorAll(".glide__slide");
    if (!container || !slides || slides.length === 0) return;

    const timer = setTimeout(() => {
      glideRef.current?.destroy();
      const instance = new Glide(`.${UNIQUE_CLASS}`, {
        type: "carousel",
        perView: 3,
        gap: 28,
        bound: true,
        focusAt: "center",
        breakpoints: {
          1280: { perView: 3, gap: 24, focusAt: "center" },
          1024: { perView: 2, gap: 20, focusAt: 0 },
          640: { perView: 1.1, gap: 16, focusAt: "center" },
        },
      });
      instance.mount();
      instance.on("run.after", () => setActiveIndex(instance.index));
      glideRef.current = instance;
    }, 80);

    return () => {
      clearTimeout(timer);
      glideRef.current?.destroy();
      glideRef.current = null;
    };
  }, [UNIQUE_CLASS, products.length]);

  return (
    <section className="relative py-16 sm:py-20 lg:py-28 bg-[#0a1628] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary2-500/10 rounded-full blur-[100px]" />
      </div>

      <div className="container relative px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 sm:mb-14">
          <div className="max-w-xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-primary-400">
              Product Range
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
              Our Products
            </h2>
            <p className="mt-4 text-sm sm:text-base text-white/55 leading-relaxed">
              Precision-engineered profiles trusted by engineers and contractors
              across the UAE and GCC.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => glideRef.current?.go("<")}
              className="w-11 h-11 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-colors"
              aria-label="Previous products"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => glideRef.current?.go(">")}
              className="w-11 h-11 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition-colors"
              aria-label="Next products"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            <Link
              to="/products"
              className="hidden sm:inline-flex items-center gap-2 ml-2 text-sm font-semibold text-white/70 hover:text-white transition-colors"
            >
              View all
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className={`${UNIQUE_CLASS} relative`}>
          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides items-stretch">
              {products.map((p) => (
                <li key={p.id} className="glide__slide h-auto">
                  <Link
                    to={p.href}
                    className="group/card flex flex-col h-full rounded-2xl overflow-hidden bg-[#0f1b3d] border border-white/10 hover:border-primary-500/40 transition-all duration-300 hover:shadow-[0_20px_50px_rgba(0,0,0,0.35)]"
                  >
                    {/* Image stage — fixed size, no stretch */}
                    <div className="relative h-[200px] sm:h-[220px] flex items-center justify-center bg-gradient-to-b from-white via-neutral-50 to-neutral-100 overflow-hidden">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(255,255,255,0.9)_0%,rgba(245,245,245,0.6)_100%)]" />
                      <SafeImage
                        src={p.image}
                        alt={p.name}
                        placeholderVariant="product"
                        placeholderCompact
                        objectFit="contain"
                        className="relative z-[1] w-full h-full max-h-[170px] sm:max-h-[190px]"
                        imgClassName="max-w-[85%] max-h-[170px] sm:max-h-[190px] mx-auto"
                      />
                    </div>

                    <div className="flex flex-col flex-1 p-5 sm:p-6">
                      <h3 className="font-semibold text-base sm:text-lg text-white group-hover/card:text-primary-300 transition-colors line-clamp-2">
                        {p.name}
                      </h3>
                      <p className="mt-2 text-sm text-white/50 leading-relaxed line-clamp-2 flex-1">
                        {p.desc}
                      </p>
                      <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-primary-400 group-hover/card:text-primary-300 transition-colors">
                        Explore range
                        <ArrowRightIcon className="w-4 h-4 group-hover/card:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {products.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => glideRef.current?.go(`=${i}`)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                activeIndex === i
                  ? "w-8 bg-primary-400"
                  : "w-4 bg-white/20 hover:bg-white/40"
              }`}
              aria-label={`Go to product ${i + 1}`}
            />
          ))}
        </div>

        <div className="text-center mt-6 sm:hidden">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-400"
          >
            View All Products
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SectionOurProducts;
